import dayjs from 'dayjs'

const DATE_FORMAT = 'DD.MM.YYYY'
const DATE_PATTERN = /^\d{2}\.\d{2}\.\d{4}$/
const MIN_DATE_YEAR = 1900

export const DATE_FORMAT_MESSAGE = 'Напишите в формате ДД.ММ.ГГГГ'

const getMaxDateYear = (): number => dayjs().year()

const parseDate = (value: string) => dayjs(value, DATE_FORMAT, true)

const isDayValidForMonth = (day: number, month: number, year = 2000): boolean => {
    if (day < 1 || month < 1 || month > 12) {
        return false
    }

    const maxDay = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).daysInMonth()

    return day <= maxDay
}

const isValidFullDate = (day: string, month: string, year: string): boolean => {
    const date = parseDate(`${day}.${month}.${year}`)

    if (!date.isValid()) {
        return false
    }

    if (date.isAfter(dayjs(), 'day')) {
        return false
    }

    const yearNumber = date.year()

    return yearNumber >= MIN_DATE_YEAR && yearNumber <= getMaxDateYear()
}

const getDateValidationError = (value: string): null | string => {
    if (!DATE_PATTERN.test(value)) {
        return DATE_FORMAT_MESSAGE
    }

    const date = parseDate(value)

    if (!date.isValid()) {
        return DATE_FORMAT_MESSAGE
    }

    if (date.isAfter(dayjs(), 'day')) {
        return 'Дата не может быть в будущем'
    }

    if (date.year() < MIN_DATE_YEAR) {
        return `Год не может быть раньше ${MIN_DATE_YEAR}`
    }

    return null
}

const canAppendDayDigit = (day: string, digit: string): boolean => {
    if (day.length === 0) {
        return /[0-3]/u.test(digit)
    }

    const firstDigit = day[0]

    if (firstDigit === '0') {
        return /[1-9]/u.test(digit)
    }

    if (firstDigit === '3') {
        return /[01]/u.test(digit)
    }

    return /\d/u.test(digit)
}

const canAppendMonthDigit = (month: string, digit: string, day: string): boolean => {
    if (month.length === 0) {
        return /[01]/u.test(digit)
    }

    const firstDigit = month[0]

    if (firstDigit === '0' && !/[1-9]/u.test(digit)) {
        return false
    }

    if (firstDigit === '1' && !/[0-2]/u.test(digit)) {
        return false
    }

    if (firstDigit !== '0' && firstDigit !== '1') {
        return false
    }

    if (day.length === 2) {
        return isDayValidForMonth(Number(day), Number(`${firstDigit}${digit}`))
    }

    return true
}

const canAppendYearDigit = (year: string, digit: string, day: string, month: string): boolean => {
    const nextYear = `${year}${digit}`
    const maxYear = getMaxDateYear()
    const minPossibleYear = Number(nextYear.padEnd(4, '0'))
    const maxPossibleYear = Number(nextYear.padEnd(4, '9'))

    if (minPossibleYear > maxYear || maxPossibleYear < MIN_DATE_YEAR) {
        return false
    }

    if (nextYear.length < 4) {
        return true
    }

    if (day.length !== 2 || month.length !== 2) {
        return true
    }

    return isValidFullDate(day, month, nextYear)
}

const sanitizeDateDigits = (digits: string): string => {
    let day = ''
    let month = ''
    let year = ''

    for (const digit of digits.slice(0, 8)) {
        if (day.length < 2) {
            if (canAppendDayDigit(day, digit)) {
                day += digit
            }

            continue
        }

        if (month.length < 2) {
            if (canAppendMonthDigit(month, digit, day)) {
                month += digit
            }

            continue
        }

        if (year.length < 4 && canAppendYearDigit(year, digit, day, month)) {
            year += digit
        }
    }

    return `${day}${month}${year}`
}

export const formatBirthDateInput = (value: string): string => {
    const digits = sanitizeDateDigits(value.replaceAll(/\D/g, ''))

    if (digits.length <= 2) {
        return digits
    }

    if (digits.length <= 4) {
        return `${digits.slice(0, 2)}.${digits.slice(2)}`
    }

    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`
}

export const validateDate = (value: string): string | true => {
    if (!value) {
        return true
    }

    if (value.replaceAll(/\D/g, '').length < 8) {
        return DATE_FORMAT_MESSAGE
    }

    const error = getDateValidationError(value)

    return error ?? true
}
